import { useState, useEffect } from 'react';

/**
 * usePWAInstall Hook
 * 
 * Enhanced PWA installation hook that provides intelligent device detection,
 * installation status tracking, and mobile-first installation prompts.
 * 
 * Key Features:
 * - Accurate mobile device detection
 * - Installation status tracking across sessions
 * - iOS-specific installation handling
 * - Desktop user filtering (no install prompts for desktop)
 * - Persistent installation state management
 * 
 * @returns {Object} An object containing:
 *   - isInstallable: boolean indicating if the PWA can be installed
 *   - isInstalled: boolean indicating if the PWA is already installed
 *   - isMobile: boolean indicating if the device is mobile
 *   - promptInstall: function to trigger the PWA installation prompt
 *   - isIOS: boolean indicating if the device is iOS
 *   - isAndroid: boolean indicating if the device is Android
 */
const usePWAInstall = () => {
  // State management for PWA installation
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);

  /**
   * Enhanced device detection with more accurate mobile identification
   * Detects various mobile devices including tablets
   * @returns {Object} Device detection results
   */
  const detectDevice = () => {
    const ua = window.navigator.userAgent;
    
    // iOS detection (iPhone, iPad, iPod)
    const isIOSDevice = /iPad|iPhone|iPod/.test(ua) || 
                        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    // Android detection
    const isAndroidDevice = /Android/.test(ua);
    
    // Mobile detection (includes tablets)
    const isMobileDevice = /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
                           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
                           window.innerWidth <= 1024; // Include small screens
    
    return {
      isIOS: isIOSDevice,
      isAndroid: isAndroidDevice,
      isMobile: isMobileDevice
    };
  };

  /**
   * Check if the app is already installed or running as a PWA
   * Uses multiple detection methods for accuracy
   * @returns {boolean} True if the app is installed or running as PWA
   */
  const checkIfInstalled = () => {
    // Check if running in standalone mode (installed PWA)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }
    
    // iOS specific standalone detection
    if (window.navigator.standalone === true || window.navigator.standalone === 'true') {
      return true;
    }
    
    // Check localStorage for installation flag
    if (localStorage.getItem('pwaInstalled') === 'true') {
      return true;
    }
    
    // Additional check for PWA context
    if (window.matchMedia('(display-mode: minimal-ui)').matches) {
      return true;
    }
    
    return false;
  };

  /**
   * Initialize device detection and installation status
   */
  useEffect(() => {
    const deviceInfo = detectDevice();
    const installed = checkIfInstalled();
    
    // Update state with device information
    setIsIOS(deviceInfo.isIOS);
    setIsAndroid(deviceInfo.isAndroid);
    setIsMobile(deviceInfo.isMobile);
    setIsInstalled(installed);
    
    // Only proceed with installation logic if not already installed and on mobile
    if (!installed && deviceInfo.isMobile) {
      // Listen for the beforeinstallprompt event (Android/Chrome)
      const handleBeforeInstallPrompt = (e) => {
        e.preventDefault();
        setInstallPromptEvent(e);
        setIsInstallable(true);
        console.log('PWA install prompt event captured');
      };
      
      // Listen for successful installation
      const handleAppInstalled = () => {
        setIsInstalled(true);
        setIsInstallable(false);
        localStorage.setItem('pwaInstalled', 'true');
        console.log('PWA was successfully installed');
        
        // Clear the install prompt event
        setInstallPromptEvent(null);
      };
      
      // Add event listeners
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.addEventListener('appinstalled', handleAppInstalled);
      
      // For iOS devices, mark as installable if conditions are met
      if (deviceInfo.isIOS && !installed) {
        setIsInstallable(true);
      }
      
      // Cleanup function
      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('appinstalled', handleAppInstalled);
      };
    }
  }, []);

  /**
   * Enhanced install prompt function with better error handling
   * Handles both Android/Chrome install prompts and iOS instructions
   */
  const promptInstall = async () => {
    try {
      if (installPromptEvent && !isInstalled) {
        // For Android/Chrome - use native install prompt
        await installPromptEvent.prompt();
        const choiceResult = await installPromptEvent.userChoice;
        
        if (choiceResult.outcome === 'accepted') {
          console.log('User accepted the install prompt');
          localStorage.setItem('pwaInstalled', 'true');
          setIsInstalled(true);
          setIsInstallable(false);
          setInstallPromptEvent(null);
        } else {
          console.log('User dismissed the install prompt');
        }
      } else if (isIOS && !isInstalled) {
        // For iOS - we'll handle this in the component that uses this hook
        console.log('iOS device detected - showing custom install instructions');
        return { type: 'ios', action: 'showInstructions' };
      } else {
        console.log('Install prompt not available or already installed');
      }
    } catch (error) {
      console.error('Error during PWA installation:', error);
    }
  };

  return { 
    isInstallable: isInstallable && isMobile && !isInstalled, // Only installable on mobile
    isInstalled, 
    isMobile,
    isIOS,
    isAndroid,
    promptInstall 
  };
};

export default usePWAInstall;
